import { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Search from 'assets/filters/searchIcon.svg';
import Configure from 'assets/unifiedCommunication/Configure.svg';
import Input from 'common-ui/input';
import MobileList from 'common-ui/mobileList';
import Switch from 'common-ui/switch';
import { Table } from 'common-ui/table';
import {
  getAllPermissionsByAccessType,
  togglePermissionStatus,
} from 'features/chat/chatActions';
import { selectAllTeamPermissions } from 'features/chat/chatSlice';
import { getTeams } from 'features/teams/teamsActions';
import { selectTeamCount, selectTeams } from 'features/teams/teamsSlice';
import useDebounce from 'hooks/useDebounce';

import ChannelConfigurator from '../components/ChannelConfigurator';
import PlatformIcons from '../components/PlatformIcons';
import { useChatSearchParams } from '../useSearchData';
import {
  ConfigureButton,
  InputWrapper,
  MobileExpandedWrapper,
  MobileLabel,
  MobileRow,
  StatusText,
  StatusWrapper,
  TableContainer,
} from './ByTeam.styles.js';

const ByTeam = () => {
  const dispatch = useDispatch();
  const count = useSelector(selectTeamCount);
  const { searchData, setChatSearchData } = useChatSearchParams();
  const teams = useSelector(selectTeams);
  const allTeamPermissions = useSelector(selectAllTeamPermissions);
  const [filterData, setFilterData] = useState({ name: searchData.name || '' });
  const debouncedName = useDebounce(filterData.name, 500);
  const [first, setFirst] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const pagesCount = Math.ceil(count / searchData.limit);
  const [channelPermissions, setChannelPermissions] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const getPermissionRecord = (teamId) => {
    return allTeamPermissions.find((perm) => perm.targetId === teamId);
  };

  const getStatus = (teamId) => {
    const permRecord = getPermissionRecord(teamId);
    if (!permRecord) return 'Disabled';
    return permRecord.isActive ? 'Active' : 'Disabled';
  };

  const handleToggleStatus = (teamId) => {
    const permRecord = getPermissionRecord(teamId);
    if (!permRecord) return;

    dispatch(
      togglePermissionStatus({
        permissionId: permRecord._id,
        isActive: !permRecord.isActive,
        accessType: 'team',
      })
    );
  };

  const parsePermissions = (teamId) => {
    const viewPlatforms = [];
    const replyPlatforms = [];

    const teamPermission = getPermissionRecord(teamId);

    if (teamPermission?.permissions) {
      teamPermission.permissions.forEach((perm) => {
        if (!perm.isDenied) {
          if (perm.type === 'view_only') {
            viewPlatforms.push(perm.platform);
          } else if (perm.type === 'view_and_answer') {
            replyPlatforms.push(perm.platform);
          }
        }
      });
    }

    return { viewPlatforms, replyPlatforms };
  };

  const currentPage = useMemo(() => {
    return Math.floor((searchData.offset || 0) / (searchData.limit || 10)) + 1;
  }, [searchData.offset, searchData.limit]);

  const handleChangeInput = (value) => {
    setFirst(false);
    setFilterData({ name: value });
  };

  const handleClearInput = () => {
    setFirst(false);
    setFilterData({ name: '' });
    setChatSearchData({ name: '' });
  };

  const columns = [
    {
      key: 'team',
      title: 'Team Name',
      dataIndex: 'team',
      render: (value) => <span>{value}</span>,
    },
    {
      key: 'viewAccess',
      title: 'View',
      dataIndex: 'id',
      render: (teamId) => {
        const { viewPlatforms } = parsePermissions(teamId);
        return <PlatformIcons platforms={viewPlatforms} />;
      },
    },
    {
      key: 'viewReply',
      title: 'View and Reply',
      dataIndex: 'id',
      render: (teamId) => {
        const { replyPlatforms } = parsePermissions(teamId);
        return <PlatformIcons platforms={replyPlatforms} />;
      },
    },
    {
      key: 'channels',
      title: 'Channels',
      dataIndex: 'channels',
      render: (_value, row) => (
        <ConfigureButton
          onClick={() => {
            setChannelPermissions({});
            setSelectedTeam(row);
            setIsChannelModalOpen(true);
          }}
        >
          <img src={Configure} alt="configure" />
          Configure
        </ConfigureButton>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'id',
      render: (teamId) => {
        const status = getStatus(teamId);
        const isActive = status === 'Active';
        const hasPermission = !!getPermissionRecord(teamId);

        return (
          <StatusWrapper $isActive={isActive}>
            <StatusText $isActive={isActive}>{status}</StatusText>
            <Switch
              activeColor="#15C7A7"
              isOn={isActive}
              onToggle={() => handleToggleStatus(teamId)}
              disabled={!hasPermission}
            />
          </StatusWrapper>
        );
      },
    },
  ];

  const mobileColumns = [
    {
      key: 'team',
      title: 'Team',
      dataIndex: 'team',
      render: (value) => <span>{value}</span>,
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'id',
      render: (teamId) => {
        const status = getStatus(teamId);
        const isActive = status === 'Active';
        const hasPermission = !!getPermissionRecord(teamId);

        return (
          <StatusWrapper $isActive={isActive}>
            <StatusText $isActive={isActive}>{status}</StatusText>
            <Switch
              activeColor="#15C7A7"
              isOn={isActive}
              onToggle={() => handleToggleStatus(teamId)}
              disabled={!hasPermission}
            />
          </StatusWrapper>
        );
      },
    },
  ];

  const renderExpandable = (row) => {
    const { viewPlatforms, replyPlatforms } = parsePermissions(row.id);

    return (
      <MobileExpandedWrapper>
        <MobileRow>
          <MobileLabel>View</MobileLabel>
          <PlatformIcons platforms={viewPlatforms} />
        </MobileRow>

        <MobileRow>
          <MobileLabel>View and Reply</MobileLabel>
          <PlatformIcons platforms={replyPlatforms} />
        </MobileRow>

        <ConfigureButton
          onClick={() => {
            setChannelPermissions({});
            setSelectedTeam(row);
            setIsChannelModalOpen(true);
          }}
        >
          <img src={Configure} alt="configure" />
          Configure
        </ConfigureButton>
      </MobileExpandedWrapper>
    );
  };

  const onPaginationChange = (page) => {
    setChatSearchData({ offset: (page - 1) * searchData.limit });
  };

  useEffect(() => {
    dispatch(getTeams({ ...searchData }));
  }, [dispatch, JSON.stringify(searchData)]);

  useEffect(() => {
    if (teams) {
      const formattedData = teams.map((team) => ({
        id: team.uuid,
        uuid: team.uuid,
        team: team.name,
        viewAccess: false,
        viewReply: false,
        status: 'Active',
      }));
      setTableData(formattedData);
    }
  }, [teams]);

  useEffect(() => {
    if (!first) {
      setChatSearchData({ name: debouncedName });
    }
  }, [debouncedName]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    dispatch(getAllPermissionsByAccessType({ accessType: 'team' }));
  }, [dispatch]);

  const handleModalClose = () => {
    setIsChannelModalOpen(false);
    dispatch(getAllPermissionsByAccessType({ accessType: 'team' }));
  };

  return (
    <div>
      <InputWrapper>
        <Input
          label="Search Team name"
          leftIcon={Search}
          value={filterData.name}
          onChange={(e) => handleChangeInput(e.target.value)}
          clearable
          onClear={handleClearInput}
        />
      </InputWrapper>

      <TableContainer>
        {isMobile ? (
          <MobileList
            columns={mobileColumns}
            data={tableData}
            expandable={renderExpandable}
            loading={false}
            currentPage={currentPage}
            totalPages={pagesCount}
            onPaginationChange={onPaginationChange}
            rowKey="id"
          />
        ) : (
          <Table
            data={tableData}
            columns={columns}
            pagination={true}
            currentPage={currentPage}
            totalPages={pagesCount}
            onPaginationChange={onPaginationChange}
          />
        )}
      </TableContainer>

      {selectedTeam && (
        <ChannelConfigurator
          isOpen={isChannelModalOpen}
          onClose={handleModalClose}
          role={selectedTeam}
          channelPermissions={channelPermissions}
          setChannelPermissions={setChannelPermissions}
          accessType="team"
        />
      )}
    </div>
  );
};

export default ByTeam;
